import { GetRef, Select } from "antd";
import { SetStateAction, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { CONVERSATION_MODE } from "../../../../../pages/MessagePage/constants";
import { messageActions } from "../../../../../pages/MessagePage/reducer";
import { ChatService } from "../../../../../util/services/chat";
import { selectMessage, selectUser } from "../../../../../util/store/selectors";
import { getStore, useSelector } from "../../../../../util/store/store";
import { CHAT_TYPE, OP_STATUS } from "../../../../../util/varibles/constants";
import { showErrorResponse } from "../../../../../util/varibles/global";
import LoadingBox from "../../../../LoadingBox";
import Id from "../../../../Operation/Id";
import Logo from "../../../../Operation/Logo";
import ChatBox from "../../../ChatBox";
import { getSubInfoChatOp } from "../../../constants";
import styles from './style.module.scss';


interface IViewType {
    className: string
    selectMode?: "multiple"
    placeholder: string
    search: typeof ChatService.searchUser | typeof ChatService.searchOp,
    keyOfResult: string
    renderOp: (item: any) => { value: any, label: any, root: any }
}

const viewType: Record<string, IViewType> = {
    [CHAT_TYPE.USER]: {
        className: "select-mode-tag",
        selectMode: "multiple",
        placeholder: 'Choose people to join the chat',
        search: ChatService.searchUser,
        keyOfResult: "users",
        renderOp: item => ({
            value: item.id,
            label: item.username,
            root: item
        }),
    },
    [CHAT_TYPE.OPERATION]: {
        className: "select-single",
        placeholder: 'Choose operation',
        search: ChatService.searchOp,
        keyOfResult: "operations",
        renderOp: item => ({
            value: item.id,
            label: <div className="flex gap-[5px] items-center">
                <Logo type={item.operation_type} status={OP_STATUS.NEW} />
                <Id code={item.name} status={OP_STATUS.NEW} />
                <div>{getSubInfoChatOp(item)}</div>
            </div>,
            root: item
        }),
    }
}

type TValues = {
    id: string,
    data: any
}[]

const Title: React.FC<{ selectRef: React.RefObject<SelectRefType>, chatType: CHAT_TYPE, values: TValues, setValues(data: SetStateAction<TValues>): void }> = ({ selectRef, chatType, values, setValues }) => {
    const [{ loading, options }, setOptions] = useState<{ loading: boolean, options: [], isMore: boolean }>({ loading: false, options: [], isMore: false });
    const [open, setOpen] = useState(false);
    const abortRef = useRef<AbortController>(new AbortController());
    const timeoutRef = useRef(setTimeout(() => { }));

    useEffect(() => {
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);
        abortRef.current?.abort();
        setOptions(prev => ({ ...prev, options: [] }))
        setOpen(false);
    }, [chatType])

    const { className, selectMode, placeholder, search, renderOp, keyOfResult } = viewType[chatType] || {};

    const handleSearch = (key: string) => {
        const value = key.trim();
        abortRef.current?.abort();
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);

        if (value.length < 2) {
            setOptions({ loading: false, options: [], isMore: false });
            setOpen(false);
            return;
        }
        setOpen(true);
        setOptions({ loading: true, options: [], isMore: false });
        timeoutRef.current = setTimeout(() => {
            abortRef.current = new AbortController();
            search({ key: value, index: 1 }, abortRef.current)
                .then((rs: any) => {
                    const { next, [keyOfResult]: result } = rs;
                    setOptions({ loading: false, options: result.map((item: any) => renderOp(item)), isMore: next });
                    setOpen(true);
                })
                .catch(er => {
                    if (er.name === 'AbortError') {
                        return;
                    }
                    showErrorResponse("Get data failure", er)
                    setOptions({ loading: false, options: [], isMore: false });
                })
        }, 380)
    }

    return <div className={styles.newConversationName} onClick={e => e.stopPropagation()}>
        <Select
            ref={selectRef}
            value={values.map(item => item.id)}
            open={open}
            onOpenChange={nextOpen => {
                if (!nextOpen)
                    setOpen(false);
            }}
            className={className}
            mode={selectMode}
            placeholder={placeholder}
            showSearch={{
                onSearch: handleSearch,
                filterOption: false,
                autoClearSearchValue: false
            }}
            styles={{ popup: { root: { zIndex: 2000 } } }}
            popupMatchSelectWidth={false}
            defaultActiveFirstOption={false}
            suffixIcon={null}
            notFoundContent={<LoadingBox loading={loading} />}
            options={options}
            onSelect={(_, opt: any) => {
                setValues([...values, { id: opt.root.id, data: opt.root }])
                if (chatType === CHAT_TYPE.OPERATION)
                    setTimeout(() => {
                        const el = document.getElementById('input-' + opt.root.id);
                        el?.focus()
                    }, 200)
            }}
            onDeselect={value => setValues(values.filter(item => item.id !== value))}
        />
    </div >
}

type SelectRefType = GetRef<typeof Select>;

const ConversationAdd: React.FC = () => {
    const chatType = useSelector(selectMessage.newConversationType);
    const dispatch = useDispatch();
    const selectRef = useRef<SelectRefType>(null);

    const [mode, setMode] = useState(CONVERSATION_MODE.CLOSE);
    const [values, setValues] = useState<TValues>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (chatType) {
            setValues([]);
            setLoading(false)
            setMode(CONVERSATION_MODE.OPEN);
            setTimeout(() => selectRef.current?.focus(), 100);
        } else {
            setMode(CONVERSATION_MODE.CLOSE);
        }
    }, [chatType])

    const changeMode = (e: React.MouseEvent, mode: CONVERSATION_MODE) => {
        const open = mode === CONVERSATION_MODE.MINIMIZE ? CONVERSATION_MODE.OPEN : CONVERSATION_MODE.MINIMIZE

        if (open === CONVERSATION_MODE.OPEN) {
            setTimeout(() => selectRef.current?.focus(), 100);
        }
        e.preventDefault();
        e.stopPropagation();

        setMode(open);
        return false
    }

    const handleAdd = (args: any) => {
        const handleAddSuccess = (chat: any, id: string) => {
            setLoading(false)
            dispatch(messageActions.setNewConversationType());
            dispatch(messageActions.updateConversations({ conversation: chat, mode: CONVERSATION_MODE.OPEN, isTop: true }));
        }
        const handleAddFailure = async (error: any) => {
            setLoading(false)
            showErrorResponse('Create chat failed', error);
        }
        const user = selectUser(getStore());
        if (chatType === CHAT_TYPE.USER) {
            const [name] = user.username.split('@');
            setLoading(true)
            ChatService.createChat({
                ...args,
                name: [name, ...values.map(item => item.data.username.split('@')[0])].join(', '),
                users: [...values.map(item => item.id), user.user_id],
                members: [...values.map(item => ({ id: item.id, username: item.data.username })), {
                    id: user.user_id,
                    username: user.username
                }],
                type: CHAT_TYPE.USER
            })
                .then((chat: any) => {
                    const { id } = chat.group_chat;
                    dispatch(messageActions.addChat(chat));
                    handleAddSuccess(chat, id);
                })
                .catch(handleAddFailure)
        } else {
            const { id, data } = values[0];
            setLoading(true)
            ChatService.sendMessage({ ...args, type: CHAT_TYPE.OPERATION, group_id: id, tenant_id: data.tenant_id })
                .then((chat: any) => {
                    dispatch(messageActions.addChatOfOp(chat));
                    handleAddSuccess(chat, id);
                })
                .catch(handleAddFailure)
        }
    }

    const close = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(messageActions.setNewConversationType());
        return false
    }

    if (!chatType)
        return null;

    return <div
        className={[styles.conversation, styles['conversation-add']].join(' ')}
        data-mode={mode}
    >
        <div className={styles.title} onClick={(e) => changeMode(e, mode)}        >
            <div className={styles['title-name']}>
                {mode === CONVERSATION_MODE.MINIMIZE
                    ? 'New chat'
                    : chatType && <Title selectRef={selectRef} chatType={chatType} values={values} setValues={setValues} />}
            </div>
            <div className='bt-close' onMouseDown={close} />
        </div>
        <div className={styles.content}>
            <ChatBox
                loading={loading}
                data={{
                    type: chatType,
                    group_chat: {
                        id: values[0] ? values[0].id : null,
                        members: [],
                    }
                }}
                isActiveInput={values.length > 0}
                onSend={handleAdd}
            />
        </div>
    </div>
}

export default ConversationAdd
